// src/api/fetch.ts
import { API_DOMAIN, API_HOST, API_PATH, DEFAULT_LOCALE } from "@/constants";
import { getEnvConfig } from "@/funcs/env";

function getApiBaseUrl(locale?: string): string {
  const effectiveLocale = locale || DEFAULT_LOCALE;
  return `https://${API_HOST}.${effectiveLocale}.${API_DOMAIN}/${API_PATH}`;
}

export const API_BASE_URL = "https://api.us.dev.saasbase.io/mgmt/v1";
export const DEFAULT_PROJECT_ID = "proj_06fghx60hnrcxftadsj44e0pj8";

export class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export interface RequestOptions extends RequestInit {
  token?: string;
}

export async function apiFetch<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { token, headers, ...rest } = options;
  const envConfig = getEnvConfig();
  const baseUrl = envConfig?.apiBaseUrl ?? getApiBaseUrl(envConfig?.locale);
  const projectId = envConfig?.projectId ?? DEFAULT_PROJECT_ID;

  const normalizedHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...envConfig?.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  if (headers) {
    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        normalizedHeaders[key] = value;
      });
    } else if (Array.isArray(headers)) {
      headers.forEach(([key, value]) => {
        normalizedHeaders[key] = value;
      });
    } else {
      Object.assign(normalizedHeaders, headers);
    }
  }

  if (
    !Object.prototype.hasOwnProperty.call(
      normalizedHeaders,
      "x-saasbase-project-id",
    )
  ) {
    normalizedHeaders["x-saasbase-project-id"] = projectId;
  }

  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...rest,
    headers: normalizedHeaders,
  });

  let data: any = null;

  try {
    data = await response.json();
  } catch {
    // ignore non-json responses
  }

  if (!response.ok) {
    throw new ApiError(
      data?.error?.message || response.statusText || "Something went wrong",
      response.status,
      data,
    );
  }

  return data as T;
}
